import { useState, useCallback, useRef, useEffect } from 'react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { rotateImage, cropImage } from '../../lib/imageUtils';

interface CroppedImage {
  blob: Blob;
  width: number;
  height: number;
}

interface ImageCropperProps {
  imageSrc: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (croppedImage: CroppedImage) => void;
}

type AspectPreset = 'free' | 'square' | '4:3' | '16:9';

const aspectPresets: { id: AspectPreset; label: string; value: number | undefined }[] = [
  { id: 'free', label: 'Bebas', value: undefined },
  { id: 'square', label: '1:1', value: 1 },
  { id: '4:3', label: '4:3', value: 4 / 3 },
  { id: '16:9', label: '16:9', value: 16 / 9 },
];

export function ImageCropper({ imageSrc, isOpen, onClose, onSave }: ImageCropperProps) {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [aspectPreset, setAspectPreset] = useState<AspectPreset>('free');
  const [currentSrc, setCurrentSrc] = useState(imageSrc);
  const [isProcessing, setIsProcessing] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Update source when imageSrc changes
  useEffect(() => {
    setCurrentSrc(imageSrc);
  }, [imageSrc]);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth: width, naturalHeight: height } = e.currentTarget;

    const preset = aspectPresets.find((p) => p.id === aspectPreset);
    if (preset?.value) {
      const newCrop = centerCrop(
        makeAspectCrop({ unit: '%', width: 90 }, preset.value, width, height),
        width,
        height
      );
      setCrop(newCrop);
    } else {
      setCrop({ unit: '%', x: 5, y: 5, width: 90, height: 90 });
    }
  }, [aspectPreset]);

  const handleAspectChange = (preset: AspectPreset) => {
    setAspectPreset(preset);
    if (imgRef.current) {
      const { naturalWidth: width, naturalHeight: height } = imgRef.current;
      const presetObj = aspectPresets.find((p) => p.id === preset);
      if (presetObj?.value) {
        const newCrop = centerCrop(
          makeAspectCrop({ unit: '%', width: 90 }, presetObj.value, width, height),
          width,
          height
        );
        setCrop(newCrop);
      }
    }
  };

  const handleRotate = async (clockwise: boolean) => {
    setIsProcessing(true);
    try {
      const rotated = await rotateImage(currentSrc, clockwise);
      setCurrentSrc(rotated);
    } catch (err) {
      console.error('Rotate failed:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!completedCrop || !imgRef.current) return;

    setIsProcessing(true);
    try {
      // Scale crop coordinates from displayed size to natural image size
      const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
      const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

      const croppedImage = await cropImage(currentSrc, {
        x: completedCrop.x * scaleX,
        y: completedCrop.y * scaleY,
        width: completedCrop.width * scaleX,
        height: completedCrop.height * scaleY,
      });
      onSave(croppedImage);
      // Don't call onClose() here - onSave handler manages modal state
    } catch (err) {
      console.error('Crop failed:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const aspect = aspectPresets.find((p) => p.id === aspectPreset)?.value;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-0 sm:p-4">
      <div className="bg-white sm:rounded-lg max-w-2xl w-full h-full sm:h-auto sm:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Edit Gambar</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full min-w-11 min-h-11 flex items-center justify-center">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Crop Area */}
        <div className="flex-1 overflow-auto p-2 sm:p-4 flex items-center justify-center bg-gray-100 touch-manipulation">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
            className="max-h-[40vh] sm:max-h-[50vh]"
          >
            <img
              ref={imgRef}
              src={currentSrc}
              alt="Crop preview"
              onLoad={onImageLoad}
              className="max-h-[40vh] sm:max-h-[50vh] object-contain"
            />
          </ReactCrop>
        </div>

        {/* Controls */}
        <div className="p-4 border-t space-y-4 pb-[env(safe-area-inset-bottom,16px)]">
          {/* Aspect Ratio Presets */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-600">Rasio:</span>
            <div className="flex gap-2">
              {aspectPresets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => handleAspectChange(preset.id)}
                  className={`px-4 py-2 text-sm rounded-full min-h-11 min-w-11 ${
                    aspectPreset === preset.id
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Rotate Buttons */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Putar:</span>
            <button
              type="button"
              onClick={() => handleRotate(false)}
              disabled={isProcessing}
              className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50 min-h-11 min-w-11 flex items-center justify-center"
              title="Putar kiri"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a4 4 0 014 4v2M3 10l6 6m-6-6l6-6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => handleRotate(true)}
              disabled={isProcessing}
              className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50 min-h-11 min-w-11 flex items-center justify-center"
              title="Putar kanan"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a4 4 0 00-4 4v2m14-6l-6 6m6-6l-6-6" />
              </svg>
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-3 text-gray-600 hover:bg-gray-100 rounded-lg min-h-12"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isProcessing || !completedCrop}
              className="px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 min-h-12 font-medium"
            >
              {isProcessing ? 'Memproses...' : 'Simpan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
